import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import { useRouter } from 'next/router';
import { NextIntlClientProvider } from 'next-intl';
import { DefaultSeo } from 'next-seo';

import { FlagsmithProvider } from 'flagsmith/react';

import { FeatureFlagService } from '@/utils/services/FeatureFlagService';

import { GlobalInfoProvider } from '@/utils/hooks/context/useGlobalInfo';

import { LoggedInStoreProvider } from '@/utils/zustand/providers/logged-in-provider';
import { ProfilesStoreProvider } from '@/utils/zustand/providers/profiles-provider';
import { SignUpStoreProvider } from '@/utils/zustand/providers/sign-up-provider';
import { ToggleLayoutPlatformProvider } from '@/utils/zustand/providers/toggle-layout-platform-provider';
import { usePartnerStackCookie } from '@/hooks/utils/usePartnerStackCookie';
import { useAppVersionCheck } from '@/utils/hooks/utils/useAppVersionCheck';
import { useUtmDataCookie } from '@/utils/hooks/utils/useUtmDataCookie';

import type { IState } from 'flagsmith/types';

import Tools from '@/components/tools';

import '../styles/styles.css';
import 'public/fonts/fonticon/style.css';
import SEO from '../../next-seo.config';
import { nextIntlErrorHandlers } from '@/i18n/config';

type AppPropsWithFlagsmith = AppProps & {
    flagsmithState?: IState;
};

function MyApp({ Component, pageProps, flagsmithState }: AppPropsWithFlagsmith) {
    const router = useRouter();

    useUtmDataCookie();
    usePartnerStackCookie();
    useAppVersionCheck();

    // Ensure we have valid props for NextIntlClientProvider
    const locale = router.locale ?? 'en';
    const messages = pageProps?.messages ?? {};

    // Get translated description from messages if available, otherwise use fallback
    const metaDescription = messages?.meta?.description;

    return (
        <>
            <DefaultSeo
                openGraph={{
                    description: metaDescription
                }}
                description={metaDescription}
                {...SEO}
            />
            <FlagsmithProvider
                flagsmith={FeatureFlagService.flagsmith}
                options={{
                    environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_KEY,
                    api: process.env.NEXT_PUBLIC_FLAGSMITH_API_URL,
                    /**
                     * Enable flag caching in localStorage to persist flags across page reloads
                     * This reduces API calls and improves performance, but flags won't update until next fetch
                     */
                    cacheFlags: false,
                    /**
                     * Allow fetching flags from the API (false = fetching is enabled)
                     * When false, the provider will fetch flags on mount and when getFlags() is called
                     * When true, it would only use serverState and never fetch updates
                     */
                    preventFetch: false
                }}
                serverState={flagsmithState}>
                <NextIntlClientProvider
                    locale={locale}
                    messages={messages}
                    {...nextIntlErrorHandlers}>
                    <ToggleLayoutPlatformProvider>
                        <LoggedInStoreProvider>
                            <ProfilesStoreProvider>
                                <SignUpStoreProvider>
                                    <GlobalInfoProvider>
                                        <Component {...pageProps} />
                                        <Tools />
                                    </GlobalInfoProvider>
                                </SignUpStoreProvider>
                            </ProfilesStoreProvider>
                        </LoggedInStoreProvider>
                    </ToggleLayoutPlatformProvider>
                </NextIntlClientProvider>
            </FlagsmithProvider>
        </>
    );
}

// IMPORTANT: getInitialProps runs in BOTH contexts:
// 1. Server-side: On initial page load (direct navigation to URL)
// 2. Client-side: On client-side navigation (using Next.js Link/Router)
// This ensures flags are available on both server-rendered and client-rendered pages
MyApp.getInitialProps = async (appContext: AppContext) => {
    // Call the default App.getInitialProps to get Component and pageProps
    const appProps = await App.getInitialProps(appContext);

    // Initialize Flagsmith and get the state
    const flagsmith = await FeatureFlagService.initialize();

    // Merge default App props with Flagsmith state
    return {
        ...appProps,
        ...flagsmith
    };
};

// This default export is required in a new `pages/_app.js` file.
export default MyApp;
